"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useRef, useCallback, useEffect, useState } from "react";
import { fadeUp, staggerContainer } from "@/lib/animations";
import { SectionLabel } from "@/components/ui/SectionLabel";
import { ButtonPrimary } from "@/components/ui/ButtonPrimary";
import { HeroBackground, HeroFade } from "@/components/ui/HeroBackground";
import { useLang } from "@/lib/LanguageContext";
import { WhyDifferentSection, AiToolSection, ValuesList, AboutCta } from "@/components/pages/about/AboutSections";
import { ScrollHint } from "@/components/ui/ScrollHint"

function ScrollableBio({
  children,
  thumbColor,
  fadeColor = "rgb(13,10,24)",
}: {
  children: React.ReactNode;
  thumbColor: string;
  fadeColor?: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartScroll = useRef(0);

  const updateThumb = useCallback(() => {
    const el = contentRef.current;
    const thumb = thumbRef.current;
    const track = trackRef.current;
    if (!el || !thumb || !track) return;
    const ratio = el.scrollTop / (el.scrollHeight - el.clientHeight);
    const trackH = track.clientHeight;
    const thumbH = Math.max(40, (el.clientHeight / el.scrollHeight) * trackH);
    thumb.style.height = `${thumbH}px`;
    thumb.style.top = `${ratio * (trackH - thumbH)}px`;
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    updateThumb();
    el.addEventListener("scroll", updateThumb, { passive: true });
    return () => el.removeEventListener("scroll", updateThumb);
  }, [updateThumb]);

  const onTrackClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = contentRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb) return;
    const trackRect = track.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const thumbH = thumb.clientHeight;
    const ratio = (clickY - thumbH / 2) / (track.clientHeight - thumbH);
    el.scrollTop = ratio * (el.scrollHeight - el.clientHeight);
  }, []);

  const onThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    dragStartY.current = e.clientY;
    dragStartScroll.current = contentRef.current?.scrollTop ?? 0;

    const onMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const el = contentRef.current;
      const track = trackRef.current;
      const thumb = thumbRef.current;
      if (!el || !track || !thumb) return;
      const delta = ev.clientY - dragStartY.current;
      const ratio = delta / (track.clientHeight - thumb.clientHeight);
      el.scrollTop =
        dragStartScroll.current + ratio * (el.scrollHeight - el.clientHeight);
    };
    const onUp = () => {
      isDragging.current = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp, { once: true });
  }, []);

  return (
    <div
      className="relative flex gap-2"
      style={{ flex: "1 1 0px", minHeight: 0 }}
    >
      {/* Scrollable content — native scrollbar hidden */}
      <div
        ref={contentRef}
        className="flex flex-col gap-3 flex-1 min-h-0"
        style={{ overflowY: "scroll", scrollbarWidth: "none" }}
      >
        <style>{`.no-scrollbar::-webkit-scrollbar{display:none}`}</style>
        {children}
        {/* Bottom padding so last line isn't under the fade */}
        <div className="flex-shrink-0 h-10" />
      </div>

      {/* Bottom fade overlay */}
      <div
        className="absolute bottom-0 left-0 right-4 h-14 pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, transparent, ${fadeColor})`,
        }}
      />

      {/* Custom scrollbar track */}
      <div
        ref={trackRef}
        className="flex-shrink-0 relative w-[2px] rounded-full cursor-pointer self-stretch"
        style={{ background: "rgba(255,255,255,0.08)" }}
        onClick={onTrackClick}
      >
        {/* Thumb */}
        <div
          ref={thumbRef}
          className="absolute left-0 right-0 rounded-full cursor-grab active:cursor-grabbing"
          style={{ background: thumbColor, minHeight: "40px", top: 0 }}
          onMouseDown={onThumbMouseDown}
        />
      </div>
    </div>
  );
}

export default function AboutPage() {
  const { t } = useLang();

  return (
    <main>
      {/* ── Hero — WHO WE ARE ── */}
      <section
        className="relative overflow-hidden min-h-[100svh] flex items-center md:items-start lg:items-center"
        style={{ background: "rgb(10,8,18)" }}
      >
        <HeroBackground src="floating-bubble.webp" />
        {/* Ambient glows */}
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            width: "900px",
            height: "900px",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -60%)",
            background:
              "radial-gradient(circle, rgba(155,89,245,0.1) 0%, transparent 65%)",
            filter: "blur(100px)",
          }}
        />
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            width: "600px",
            height: "600px",
            left: "50%",
            bottom: "-100px",
            transform: "translateX(-50%)",
            background:
              "radial-gradient(circle, rgba(224,64,160,0.08) 0%, transparent 65%)",
            filter: "blur(80px)",
          }}
        />
        <HeroFade />

        <div className="container relative z-10 w-full">
          <div className="pt-24 pb-24 md:pt-40 md:pb-20 lg:pt-44 lg:pb-24">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="relative isolate flex flex-col items-center text-center gap-7 w-full max-w-4xl mx-auto"
            >
              {/* Phones: dark, blurred pool behind the copy so it reads over the image */}
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-x-10 -inset-y-16 -z-10 sm:hidden"
                style={{
                  background:
                    "radial-gradient(ellipse 65% 55% at 50% 50%, rgba(10,8,18,0.9) 0%, rgba(10,8,18,0.65) 50%, rgba(10,8,18,0) 100%)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  maskImage: "radial-gradient(ellipse 60% 55% at 50% 50%, #000 45%, transparent 100%)",
                  WebkitMaskImage: "radial-gradient(ellipse 60% 55% at 50% 50%, #000 45%, transparent 100%)",
                }}
              />
              <motion.div variants={fadeUp}>
                <SectionLabel>{t.about.pageLabel}</SectionLabel>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="font-display font-extrabold leading-[0.92] tracking-tight"
                style={{ letterSpacing: "-0.03em" }}
              >
                {/* Line 1 — "Млади." */}
                <span
                  className="block"
                  style={{
                    fontSize: "clamp(56px, 8vw, 112px)",
                    background:
                      "linear-gradient(135deg, #FFB76C 0%, #FF5E9E 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {`${t.about.pageTitle.split(". ")[0]}. `}
                </span>
                {/* Line 2 — "С богат опит." */}
                <span
                  className="block"
                  style={{
                    fontSize: "clamp(56px, 8vw, 112px)",
                    background:
                      "linear-gradient(135deg, #FFB76C 0%, #FF5E9E 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {`${t.about.pageTitle.split(". ")[1]}. `}
                </span>
                {/* Line 3 — smaller, white */}
                <span
                  className="block text-white mt-1"
                  style={{ fontSize: "clamp(28px, 3.8vw, 54px)" }}
                >
                  {t.about.pageTitle.split(". ").slice(2).join(". ")}
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="hidden sm:block text-white/80 leading-relaxed max-w-2xl text-balance"
                style={{ fontSize: "clamp(16px, 1.3vw, 20px)" }}
              >
                {t.about.pageDescription}
              </motion.p>

              <motion.div variants={fadeUp}>
                <Link href="/contact">
                  <ButtonPrimary size="lg">{t.about.ctaButton}</ButtonPrimary>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
        <ScrollHint />
      </section>

      <WhyDifferentSection />

      {/* ── Founders ── */}
      <section
        className="section-padding relative overflow-hidden"
        style={{ background: "rgb(10,8,18)" }}
      >
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            width: "800px",
            height: "800px",
            left: "-200px",
            top: "50%",
            transform: "translateY(-50%)",
            background:
              "radial-gradient(circle, rgba(224,64,160,0.08) 0%, transparent 65%)",
            filter: "blur(100px)",
          }}
        />
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            width: "700px",
            height: "700px",
            right: "-150px",
            top: "50%",
            transform: "translateY(-50%)",
            background:
              "radial-gradient(circle, rgba(155,89,245,0.09) 0%, transparent 65%)",
            filter: "blur(100px)",
          }}
        />

        <div className="container relative z-10">
          {/* Founder cards — full-width, image panel left, bio right */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex flex-col gap-6"
          >
            {/* Vyara */}
            <motion.div
              variants={fadeUp}
              className="group relative rounded-[28px] overflow-hidden lg:h-[480px]"
              style={{
                background: "rgb(13,10,24)",
                border: "1px solid rgba(224,64,160,0.12)",
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-[1px] z-10"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, #E040A0 20%, rgba(224,64,160,0.3) 55%, transparent 100%)",
                }}
              />
              <div
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at 0% 50%, rgba(224,64,160,0.06) 0%, transparent 60%)",
                }}
              />

              <div className="relative grid grid-cols-1 lg:grid-cols-[30%_1fr] lg:h-full">
                <div className="relative aspect-[3/4] lg:aspect-auto lg:h-auto overflow-hidden">
                  <Image
                    src="/team/vyara-ilieva.webp?v=3"
                    alt="Vyara Ivanova-Ilieva"
                    fill
                    className="object-cover object-top"
                  />
                  {/* Fade into the card: downwards on mobile, sideways on desktop */}
                  <div
                    className="absolute inset-0 lg:hidden"
                    style={{
                      background:
                        "linear-gradient(180deg, transparent 55%, rgb(13,10,24) 100%)",
                    }}
                  />
                  <div
                    className="absolute inset-0 hidden lg:block"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent 60%, rgba(13,10,24,0.08) 70%, rgba(13,10,24,0.22) 78%, rgba(13,10,24,0.42) 85%, rgba(13,10,24,0.65) 91%, rgba(13,10,24,0.85) 96%, rgb(13,10,24) 100%)",
                    }}
                  />
                </div>

                <div className="flex flex-col h-[680px] lg:h-[480px] p-6 sm:p-8 lg:p-10 lg:pl-6 overflow-hidden">
                  <div className="flex-shrink-0 mb-5">
                    <p
                      className="text-xs font-semibold uppercase tracking-[0.22em]"
                      style={{ color: "#E040A0" }}
                    >
                      {t.about.founder1Role}
                    </p>
                    <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-white leading-tight mt-1">
                      {t.about.founder1Name}
                    </h3>
                  </div>

                  <ScrollableBio thumbColor="#E040A0">
                    {t.about.founder1Bio.split("\n\n").map((para, i) => (
                      <p
                        key={i}
                        className="text-sm sm:text-base text-white/55 leading-relaxed"
                      >
                        {para}
                      </p>
                    ))}
                  </ScrollableBio>

                  <FounderTags tags={t.about.founder1Tags} moreLabel={t.about.moreTags} />
                </div>
              </div>
            </motion.div>

            {/* Lyubomir — warm orange accent */}
            <motion.div
              variants={fadeUp}
              className="group relative rounded-[28px] overflow-hidden lg:h-[480px]"
              style={{
                background: "rgb(13,10,24)",
                border: "1px solid rgba(255,183,108,0.12)",
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-[1px] z-10"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, #FFB76C 20%, rgba(255,183,108,0.3) 55%, transparent 100%)",
                }}
              />
              <div
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at 0% 50%, rgba(255,183,108,0.06) 0%, transparent 60%)",
                }}
              />

              <div className="relative grid grid-cols-1 lg:grid-cols-[30%_1fr] lg:h-full">
                <div className="relative h-80 sm:h-[420px] lg:h-auto overflow-hidden">
                  <Image
                    src="/team/lyubomir-iliev.webp"
                    alt="Lyubomir Iliev"
                    fill
                    className="object-cover object-top"
                  />
                  {/* Fade into the card: downwards on mobile, sideways on desktop */}
                  <div
                    className="absolute inset-0 lg:hidden"
                    style={{
                      background:
                        "linear-gradient(180deg, transparent 55%, rgb(13,10,24) 100%)",
                    }}
                  />
                  <div
                    className="absolute inset-0 hidden lg:block"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent 55%, rgb(13,10,24) 100%)",
                    }}
                  />
                </div>

                <div className="flex flex-col lg:h-full p-6 sm:p-8 lg:p-10 lg:pl-6 lg:overflow-hidden">
                  <div className="flex-shrink-0 mb-5">
                    <p
                      className="text-xs font-semibold uppercase tracking-[0.22em]"
                      style={{ color: "#FFB76C" }}
                    >
                      {t.about.founder2Role}
                    </p>
                    <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-white leading-tight mt-1">
                      {t.about.founder2Name}
                    </h3>
                  </div>

                  <div className="flex flex-col gap-3 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
                    {t.about.founder2Bio.split("\n\n").map((para, i) => (
                      <p
                        key={i}
                        className="text-sm sm:text-base text-white/55 leading-relaxed"
                      >
                        {para}
                      </p>
                    ))}
                  </div>

                  <div className="flex-shrink-0 flex flex-wrap gap-2 pt-4">
                    {t.about.founder2Tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs font-medium px-3 py-1 rounded-full"
                        style={{
                          background: "rgba(255,183,108,0.08)",
                          border: "1px solid rgba(255,183,108,0.22)",
                          color: "rgba(255,183,108,0.85)",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          <ValuesList />
          </motion.div>
        </div>
      </section>

      <AiToolSection />

      <AboutCta />
    </main>
  );
}

/** Phones show the first few skills plus a "+N more" pill that reveals the rest */
const MOBILE_TAG_LIMIT = 4;
function FounderTags({ tags, moreLabel }: { tags: string[]; moreLabel: string }) {
  const [expanded, setExpanded] = useState(false);
  const hidden = tags.length - MOBILE_TAG_LIMIT;
  const pill = "text-xs font-medium px-3 py-1 rounded-full";
  const style = {
    background: "rgba(224,64,160,0.08)",
    border: "1px solid rgba(224,64,160,0.22)",
    color: "rgba(224,64,160,0.85)",
  };
  return (
    <div className="flex-shrink-0 flex flex-wrap gap-2 pt-4">
      {tags.map((tag, i) => (
        <span
          key={tag}
          className={`${pill} ${!expanded && i >= MOBILE_TAG_LIMIT ? "max-lg:hidden" : ""}`}
          style={style}
        >
          {tag}
        </span>
      ))}
      {!expanded && hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={`${pill} lg:hidden cursor-pointer`}
          style={{ ...style, background: "rgba(224,64,160,0.16)", color: "#fff" }}
        >
          {moreLabel.replace("{n}", String(hidden))}
        </button>
      )}
    </div>
  );
}
